import React, { Fragment, useState } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  useGetDeliveriesReportQuery,
  useGetDeliveriesMatrixQuery,
  useLazyGetDeliveryReportExcelQuery,
} from '../../redux/api/locationApi';
import { downloadBlob } from '../../utils/download';
import { useApiErrorToast } from '../../hooks/useApiError';

// Destination type -> heading, matching the wording used elsewhere in the app.
const GROUP_TITLES = {
  crew: 'Crew Lieferziele',
  backstage: 'Backstage/Bühnen Lieferziele',
};

// Zero reads as noise in a wide grid; a muted dash keeps the delivered
// quantities scannable.
const Quantity = ({ value }) =>
  value ? <span>{value}</span> : <span className='text-muted'>—</span>;

// Locations as rows, delivered articles as columns — the same pivot the Excel
// export renders (one sheet per group).
const MatrixTable = ({ group }) => (
  <Fragment>
    <h5 className='mt-4'>{GROUP_TITLES[group.type] || group.label}</h5>
    {group.rows.length === 0 ? (
      <p className='text-muted'>Keine Lieferziele für dieses Event.</p>
    ) : (
      <div className='table-responsive'>
        <table className='table table-striped table-sm align-middle text-start'>
          <thead>
            <tr>
              <th className='text-nowrap'>Lieferziel</th>
              {group.articles.map((article) => (
                <th key={article.id} className='text-end'>
                  {article.name}
                </th>
              ))}
              <th className='text-end'>Summe</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => (
              <tr key={row.destination_id}>
                <td className='text-nowrap'>{row.destination_name}</td>
                {group.articles.map((article) => (
                  <td key={article.id} className='text-end'>
                    <Quantity value={row.quantities[article.id]} />
                  </td>
                ))}
                <td className='text-end'>
                  <b>
                    <Quantity value={row.total} />
                  </b>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Summe</th>
              {group.articles.map((article) => (
                <th key={article.id} className='text-end'>
                  {group.column_totals[article.id]}
                </th>
              ))}
              <th className='text-end'>{group.total}</th>
            </tr>
          </tfoot>
        </table>
      </div>
    )}
  </Fragment>
);

const DetailTable = ({ title, rows, contingentLabel }) => (
  <Fragment>
    <h5 className='mt-4'>{title}</h5>
    <table className='table table-striped mt-4 align-middle text-start'>
      <thead>
        <tr>
          <th>Lieferziel</th>
          <th width={'320px'}>Artikel</th>
          <th>geliefert</th>
          <th>{contingentLabel}</th>
          <th>noch übrig</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((line, index) => (
          <tr key={`${line.destination_id}-${line.article_id}-${index}`}>
            <td>{line.destination_name}</td>
            <td>{line.article_name}</td>
            <td>{line.delivered_quantity}</td>
            <td>{line.contingent ?? '—'}</td>
            <td>{line.remaining ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Fragment>
);

const LocationReport = () => {
  const curEvent = useSelector((state) => state.auth.currentEvent);
  const [view, setView] = useState('matrix');
  const { data: matrixData } = useGetDeliveriesMatrixQuery(curEvent?.id, {
    skip: !curEvent?.id,
  });
  const { data: reportData } = useGetDeliveriesReportQuery(curEvent?.id, {
    skip: !curEvent?.id || view !== 'detail',
  });
  const [fetchExcel] = useLazyGetDeliveryReportExcelQuery();
  const [exporting, setExporting] = useState(false);
  const apiErrorToast = useApiErrorToast();

  const handleExcelExport = async () => {
    if (!curEvent?.id) return;
    setExporting(true);
    try {
      const blob = await fetchExcel(curEvent.id).unwrap();
      downloadBlob(blob, `lieferreport-${curEvent.name || curEvent.id}.xlsx`);
    } catch (err) {
      apiErrorToast(err, 'Excel-Export konnte nicht erstellt werden');
    } finally {
      setExporting(false);
    }
  };

  if (!curEvent?.id) {
    return <p>Bitte zuerst ein Event auswählen.</p>;
  }

  return (
    <Fragment>
      <div className='d-flex justify-content-between align-items-center'>
        <h4 className='mb-0'>Übersicht aller Lieferungen</h4>
        <div className='d-flex gap-2'>
          <ButtonGroup size='sm'>
            <Button
              variant={view === 'matrix' ? 'secondary' : 'outline-secondary'}
              onClick={() => setView('matrix')}
            >
              Matrix
            </Button>
            <Button
              variant={view === 'detail' ? 'secondary' : 'outline-secondary'}
              onClick={() => setView('detail')}
            >
              Detail
            </Button>
          </ButtonGroup>
          <Button
            variant='outline-success'
            size='sm'
            onClick={handleExcelExport}
            disabled={exporting}
          >
            <i className='fas fa-file-excel me-1' />
            Excel-Export
          </Button>
        </div>
      </div>
      <hr />
      {view === 'matrix' ? (
        !matrixData ? (
          <p>Lieferungen werden geladen …</p>
        ) : (
          matrixData.groups.map((group) => (
            <MatrixTable key={group.type} group={group} />
          ))
        )
      ) : !reportData ? (
        <p>Lieferungen werden geladen …</p>
      ) : (
        <Fragment>
          <DetailTable
            title={GROUP_TITLES.crew}
            rows={reportData.crew_locations_report || []}
            contingentLabel='Kontingent'
          />
          <DetailTable
            title={GROUP_TITLES.backstage}
            rows={reportData.backstage_locations_report || []}
            contingentLabel='Kontingent'
          />
        </Fragment>
      )}
    </Fragment>
  );
};

export default LocationReport;
